import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetProducts,
  CreateProduct,
  UpdateProduct,
  ToggleProductStatus,
  DeleteProduct,
  ImportProducts,
  ToggleProductPosStatus,
  ToggleProductNegativeQtyStatus,
  GetPosProducts,
  ToggleProductIsOnWeb,
  UpdateWebProductSettings,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import {
  DEFAULT_STALE_TIME,
  LOOKUP_STALE_TIME,
  STALE_TIME_0,
} from "../Constant/AppConstants";

export const productQueryKeys = {
  products: (params?: Parameters<typeof GetProducts>[0]) =>
    ["products", params] as const,
  posProducts: () => ["posProducts"] as const,
};

export const invalidateProductQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["products"] });
  queryClient.invalidateQueries({ queryKey: ["posProducts"] });
};

export const useProductsQuery = (
  params: Parameters<typeof GetProducts>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: STALE_TIME_0,
    ...options,
    queryKey: productQueryKeys.products(params),
    queryFn: async () => {
      const res = await GetProducts(params);
      if (!res?.success) {
        return { data: [], total: 0 };
      }
      return {
        data: res.data || [],
        total: res.meta?.total || 0,
      };
    },
  });
};

export const useProductsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetProducts>[0]) =>
      queryClient.fetchQuery({
        staleTime: STALE_TIME_0,
        queryKey: productQueryKeys.products(params),
        queryFn: async () => {
          const res = await GetProducts(params);
          if (!res?.success) {
            return { data: [], total: 0 };
          }
          return {
            data: res.data || [],
            total: res.meta?.total || 0,
          };
        },
      }),
  });
};

export const usePosProductsQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: STALE_TIME_0,
    ...options,
    queryKey: productQueryKeys.posProducts(),
    queryFn: async () => unwrapApiResponse(await GetPosProducts()),
  });
};

export const usePosProductsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: STALE_TIME_0,
        queryKey: productQueryKeys.posProducts(),
        queryFn: async () => unwrapApiResponse(await GetPosProducts()),
      }),
  });
};

export const useCreateProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateProduct>[0]) =>
      CreateProduct(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateProductQueries(queryClient);
      }
    },
  });
};

export const useUpdateProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateProduct>[0]) =>
      UpdateProduct(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateProductQueries(queryClient);
      }
    },
  });
};

const updateProductInCache = (
  queryClient: any,
  productId: string | number,
  updates: any,
) => {
  const targetId = String(productId);
  queryClient.setQueriesData({ queryKey: ["products"] }, (oldData: any) => {
    if (!oldData || !oldData.data) return oldData;
    return {
      ...oldData,
      data: oldData.data.map((p: any) =>
        String(p.id) === targetId ? { ...p, ...updates } : p,
      ),
    };
  });

  queryClient.setQueriesData({ queryKey: ["posProducts"] }, (oldData: any) => {
    if (!oldData || !Array.isArray(oldData)) return oldData;

    if (updates.status === "inactive" || updates.is_pos === 0) {
      return oldData.filter((p: any) => String(p.id) !== targetId);
    }

    return oldData.map((p: any) =>
      String(p.id) === targetId ? { ...p, ...updates } : p,
    );
  });
};

export const useToggleProductStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      id: Parameters<typeof ToggleProductStatus>[0];
      status: Parameters<typeof ToggleProductStatus>[1];
    }) => ToggleProductStatus(payload.id, payload.status),
    onSuccess: (res, variables) => {
      if (res?.success) {
        updateProductInCache(queryClient, variables.id, {
          status: variables.status,
        });
      }
    },
  });
};

export const useDeleteProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteProduct>[0]) =>
      DeleteProduct(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateProductQueries(queryClient);
      }
    },
  });
};

export const useImportProductsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof ImportProducts>[0]) =>
      ImportProducts(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateProductQueries(queryClient);
      }
    },
  });
};

export const useToggleProductPosStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      id: Parameters<typeof ToggleProductPosStatus>[0];
      is_pos: Parameters<typeof ToggleProductPosStatus>[1];
    }) => ToggleProductPosStatus(payload.id, payload.is_pos),
    onSuccess: (res, variables) => {
      if (res?.success) {
        if (variables.is_pos === 1) {
          queryClient.invalidateQueries({ queryKey: ["posProducts"] });
        }
        updateProductInCache(queryClient, variables.id, {
          is_pos: variables.is_pos,
        });
      }
    },
  });
};

export const useToggleProductNegativeQtyStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      id: Parameters<typeof ToggleProductNegativeQtyStatus>[0];
      is_negative_qty_allowed: Parameters<
        typeof ToggleProductNegativeQtyStatus
      >[1];
    }) =>
      ToggleProductNegativeQtyStatus(
        payload.id,
        payload.is_negative_qty_allowed,
      ),
    onSuccess: (res, variables) => {
      if (res?.success) {
        updateProductInCache(queryClient, variables.id, {
          is_negative_qty_allowed: variables.is_negative_qty_allowed,
        });
      }
    },
  });
};

export const useToggleProductIsOnWebMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      id: Parameters<typeof ToggleProductIsOnWeb>[0];
      is_on_web: Parameters<typeof ToggleProductIsOnWeb>[1];
    }) => ToggleProductIsOnWeb(payload.id, payload.is_on_web),
    onSuccess: (res, variables) => {
      if (res?.success) {
        updateProductInCache(queryClient, variables.id, {
          is_on_web: variables.is_on_web,
        });
      }
    },
  });
};

export const useUpdateWebProductSettingsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      id: number | string;
      settings: Record<string, unknown>;
    }) => UpdateWebProductSettings(payload.id, payload.settings),
    onSuccess: (res, variables) => {
      if (res?.success) {
        updateProductInCache(queryClient, variables.id, variables.settings);
        queryClient.invalidateQueries({ queryKey: ["products"] });
      }
    },
  });
};

export const useProductService = (config?: {
  fetchProductsParams?: Parameters<typeof GetProducts>[0];
  fetchProductsEnabled?: boolean;
  fetchPosProductsEnabled?: boolean;
}) => {
  const productsQuery = useProductsQuery(config?.fetchProductsParams, {
    enabled:
      config?.fetchProductsEnabled !== false &&
      config?.fetchProductsParams !== undefined &&
      config?.fetchProductsParams !== null,
  });

  const posProductsQuery = usePosProductsQuery({
    enabled: config?.fetchPosProductsEnabled !== false,
  });

  const createProductMutation = useCreateProductMutation();
  const updateProductMutation = useUpdateProductMutation();
  const toggleProductStatusMutation = useToggleProductStatusMutation();
  const deleteProductMutation = useDeleteProductMutation();
  const importProductsMutation = useImportProductsMutation();
  const toggleProductPosStatusMutation = useToggleProductPosStatusMutation();
  const toggleProductNegativeQtyStatusMutation =
    useToggleProductNegativeQtyStatusMutation();
  const toggleProductIsOnWebMutation = useToggleProductIsOnWebMutation();

  return {
    productsQuery,
    posProductsQuery,
    createProductMutation,
    updateProductMutation,
    toggleProductStatusMutation,
    deleteProductMutation,
    importProductsMutation,
    toggleProductPosStatusMutation,
    toggleProductNegativeQtyStatusMutation,
    toggleProductIsOnWebMutation,
  };
};
